import { FieldWrapper } from './FieldWrapper';
import clsx from 'clsx';
import Select, { GroupBase, Props } from 'react-select';

export type Option<ValueT = string> = {
  label: string;
  value: ValueT;
};

type SelectFieldProps<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = Props<Option, IsMulti, Group> & {
  label?: React.ReactNode;
  appearance?: 'default' | 'design-system';
  designSystemSize?: 'md' | 'lg';
};

export const SelectField = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  label,
  appearance = 'default',
  designSystemSize = 'lg',
  ...props
}: SelectFieldProps<Option, IsMulti, Group>) => {
  return (
    <FieldWrapper label={label} appearance={appearance}>
      <Select
        {...props}
        classNames={
          appearance === 'design-system'
            ? {
                control: ({ isDisabled, isFocused }) =>
                  clsx(
                    '!rounded-xl !border-[1.5px] !shadow-none transition-colors hover:!border-rw-sub',
                    designSystemSize === 'md'
                      ? '!h-9 !min-h-9 !px-0'
                      : '!h-11 !min-h-11 !px-0.5',
                    isFocused
                      ? '!border-primary-teal'
                      : '!border-rw-neutral-off-white',
                    isDisabled &&
                      '!border-rw-neutral-lighter-gray !bg-rw-neutral-off-white !opacity-60'
                  ),
                valueContainer: () => '!px-2 !py-0',
                singleValue: () => '!text-base !text-rw-neutral-darker-gray',
                input: () => '!m-0 !p-0 !text-base',
                indicatorSeparator: () => '!hidden',
                dropdownIndicator: () =>
                  designSystemSize === 'md'
                    ? '!p-0 !pr-2 !text-rw-neutral-dark-gray hover:!text-rw-sub'
                    : '!p-2 !text-rw-neutral-dark-gray hover:!text-rw-sub',
                indicatorsContainer: () =>
                  designSystemSize === 'md' ? '!h-9' : '!h-11',
                menu: () => '!overflow-hidden !rounded-xl !shadow-pop-out',
                option: ({ isFocused, isSelected }) =>
                  clsx(
                    '!text-base',
                    isSelected && '!bg-primary-teal',
                    isFocused && !isSelected && '!bg-rw-neutral-off-white'
                  ),
              }
            : props.classNames
        }
        styles={
          appearance === 'design-system'
            ? {
                control: (base) => ({ ...base, boxShadow: 'none' }),
              }
            : props.styles
        }
      />
    </FieldWrapper>
  );
};

export const SmallSelectField = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  transparent,
  ...props
}: SelectFieldProps<Option, IsMulti, Group> & { transparent?: true }) => {
  const transparentStyle = transparent && {
    backgroundColor: 'transparent',
    border: 'none',
    boxShadow: 'none',
  };

  return (
    <SelectField
      {...props}
      className="text-sm"
      styles={{
        control: (provided) => ({
          ...provided,
          minHeight: '25px',
          height: '25px',
          ...transparentStyle,
        }),
        valueContainer: (provided) => ({
          ...provided,
          minHeight: '25px',
          height: '25px',
          lineHeight: '25px',
          padding: '0px',
          paddingLeft: '6px',
        }),
        placeholder: (provided) => ({
          ...provided,
          margin: 0,
        }),
        input: (provided) => ({
          ...provided,
          margin: '0px',
          height: '25px',
        }),
        indicatorSeparator: () => ({
          display: 'none',
        }),
        indicatorsContainer: (provided) => ({
          ...provided,
          height: '25px',
        }),
        dropdownIndicator: (provided) => ({
          ...provided,
          height: '25px',
          padding: 2,
        }),
        option: (provided) => ({
          ...provided,
          height: '25px',
          lineHeight: '25px',
          padding: '0 12px',
        }),
        menu: (provided) => ({
          ...provided,
          width: 'max-content',
          right: 0,
        }),
      }}
      classNames={{
        option: ({ isFocused, isSelected }) =>
          clsx(
            isSelected && '!bg-rw-sub !text-white',
            isFocused && !isSelected && '!bg-rw-sub/10'
          ),
      }}
    />
  );
};
